// Invoices and payments › Signatures and numbers' Document numbers on the SettingsPage pattern (BL-189):
// how invoices and estimates are numbered (numbering.get / numbering.set), as Numbering.tsx draws them
// for Riverstone, with nothing changed. A prefix and the next number, e.g. INV- and 18316, carry on the
// company's own sequence; Save keeps them; numbers already given stay as they are, and the next can't go
// back over one given (the server refuses, in its words). Someone who can't change them reads the prefix.
import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { Link, TextField } from '@flowology/ui-web/fl';
import { SettingGroup, SettingRow, type SettingState } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { nextOk, prefixOk } from './numbering-model.ts';

interface Format {
  prefix: string;
  nextNumber: number;
  next: string;
  lastGiven: string | null;
}
type Kind = 'invoice' | 'estimate';
const LABEL: Record<Kind, string> = { invoice: 'Invoices', estimate: 'Estimates' };

function NumberRow({
  kind,
  format,
  canEdit,
  onSaved,
}: {
  kind: Kind;
  format: Format;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [prefix, setPrefix] = useState(format.prefix);
  const [next, setNext] = useState(String(format.nextNumber));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = prefix !== format.prefix || next.trim() !== String(format.nextNumber);
  const valid = prefixOk(prefix) && nextOk(next);
  const save = useSaveKey(`${kind}:${prefix}:${next}`);
  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call(
        'numbering.set',
        { kind, prefix, nextNumber: Number(next) },
        { idempotencyKey: save.key },
      );
      save.renew();
      await onSaved();
    } catch (e) {
      setError(e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  };
  const state: SettingState | undefined = busy
    ? { status: 'saving' }
    : error
      ? { status: 'failed', text: error }
      : undefined;
  return (
    <SettingRow
      name={LABEL[kind]}
      help={`Next: ${valid ? `${prefix}${Number(next)}` : format.next}${format.lastGiven ? ` · last given ${format.lastGiven}` : ''}`}
      control={
        !canEdit ? (
          format.prefix ? (
            `Prefix ${format.prefix}`
          ) : (
            'No prefix'
          )
        ) : (
          <>
            <TextField
              label={`${LABEL[kind]} prefix`}
              hideLabel
              width="short"
              value={prefix}
              placeholder="No prefix"
              error={prefixOk(prefix) ? null : 'Letters, digits and dashes only, up to 10.'}
              onChange={setPrefix}
            />
            <TextField
              label={`Next ${kind} number`}
              hideLabel
              width="number"
              inputMode="numeric"
              value={next}
              onChange={setNext}
            />
            <Link isDisabled={!changed || !valid || busy} onPress={() => void onSave()}>
              Save
            </Link>
          </>
        )
      }
      {...(state ? { state } : {})}
    />
  );
}

/** Document numbers: the invoices' and the estimates', inside Signatures and numbers. */
export function DocumentNumbers({ canEdit }: { canEdit: boolean }) {
  const [data, setData] = useState<{ invoice: Format; estimate: Format } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    try {
      setData(await client.call('numbering.get', {}));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t load the numbers.');
    }
  };
  useEffect(() => {
    void load();
  }, []);
  return (
    <SettingGroup
      label="Document numbers"
      line="Each new invoice and estimate gets the next number, with its prefix: the office, the iPad, the customer’s page, texts, emails and PDFs all show it. Numbers already given don’t change, and none is ever used twice."
    >
      {error ? (
        <SettingRow name="Document numbers" control="" state={{ status: 'failed', text: error }} />
      ) : null}
      {data
        ? (['invoice', 'estimate'] as const).map((k) => (
            <NumberRow
              key={`${k}:${data[k].prefix}:${data[k].nextNumber}`}
              kind={k}
              format={data[k]}
              canEdit={canEdit}
              onSaved={load}
            />
          ))
        : null}
    </SettingGroup>
  );
}
